import React from "react";
import { cn } from "../../lib/utils";
import { AlertCircle, Info, AlertTriangle, CheckCircle, Bell, X, ExternalLink } from "lucide-react";

// Map notification types to icon components
const typeIconMap = {
  alert: AlertCircle,
  info: Info,
  warning: AlertTriangle,
  success: CheckCircle,
  default: Bell
};

// Map notification types to colors
const typeColorMap = {
  alert: {
    icon: "text-amber-500",
    bg: "bg-amber-50"
  },
  info: {
    icon: "text-blue-500",
    bg: "bg-blue-50"
  },
  warning: {
    icon: "text-orange-500",
    bg: "bg-orange-50"
  },
  success: {
    icon: "text-green-500",
    bg: "bg-green-50"
  },
  default: {
    icon: "text-purple-500",
    bg: "bg-purple-50"
  }
};

// Map priorities to visual treatments
const priorityEffects = {
  high: "border-l-2 border-red-500",
  medium: "border-l border-amber-300",
  low: "border-l border-gray-200",
  default: ""
};

export default function StaffNotificationItem({ 
  notification, 
  isHovered, 
  onHover, 
  onLeave, 
  onClick,
  onDelete 
}) {
  const IconComponent = typeIconMap[notification.type] || typeIconMap.default;
  const { icon: iconColor, bg: iconBg } = typeColorMap[notification.type] || typeColorMap.default;
  const priorityStyle = priorityEffects[notification.priority] || priorityEffects.default;

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(notification);
  };

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-2 rounded-md transition-all duration-200",
        isHovered 
          ? "bg-white shadow-md scale-[1.02] relative z-10 border border-gray-100" 
          : "hover:bg-slate-50 hover:scale-[1.01]",
        "cursor-pointer group",
        priorityStyle
      )}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={onClick}
    >
      <div className={cn(
        "rounded-full p-1.5 flex items-center justify-center transition-transform",
        iconBg,
        isHovered && "scale-110"
      )}>
        <IconComponent className={cn(
          "h-3.5 w-3.5",
          iconColor,
          isHovered && "animate-pulse"
        )} />
      </div>
      
      <div className="flex-1 space-y-0.5 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={cn(
            "text-sm font-medium leading-none truncate transition-all flex-1",
            isHovered && "text-gray-900 font-semibold scale-[1.02]"
          )}>
            {notification.title}
          </p>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {notification.link && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(notification.link, '_blank');
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                title="Open link"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              onClick={handleDelete}
              className="p-1 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100"
              title="Delete notification"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground line-clamp-1">
          {notification.description}
        </p>
        <p className="text-xs text-muted-foreground/60">
          {new Date(notification.date).toLocaleString()}
        </p>
      </div>
      
      {!notification.read && (
        <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
      )}
    </div>
  );
}